// apps/web/src/lib/pwaScope.ts
//
// Fonte única da verdade de "esta rota NÃO participa do PWA" (Opção B2).
//
// Fluxos públicos de USO ÚNICO do cliente (magic link, vistoria, view pública da
// cotação, landing de cotação, troca de titularidade) são abertos uma vez a
// partir de um link (SMS/WhatsApp/e-mail) e nunca revisitados.
// Neles o PWA só atrapalha: um service worker precacheia o bundle e serve HTML/JS
// velho depois de um deploy, e o manifest oferece "instalar app" para uma tela
// descartável. Por isso, nessas rotas:
//   - NÃO registramos o service worker (App não monta o PwaUpdatePrompt);
//   - desregistramos qualquer SW de visita anterior + limpamos o precache;
//   - removemos o <link rel="manifest"> do DOM → some o "instalar app" (inclusive
//     o "Adicionar à tela de início" do iOS) e a página fica sempre fresca.
//
// O PWA CONTINUA ativo no /login e em todo o app autenticado (o consultor volta
// sempre e quer instalar).

// Prefixos que casam a forma "com barra" (`/p/` casa `/p` e `/p/qualquer-coisa`).
// NOTA: `/cadastro/:referralCode` NÃO entra aqui — é o funil de indicação →
// cadastro de CONSULTOR (redireciona para /registration, que MANTÉM PWA), não um
// fluxo de uso único do cliente.
const PUBLIC_NO_PWA_SLASH_PREFIXES = [
  '/p/', // magic link do associado + vistoria (/p/:token[/...])
  '/q/', // view pública read-only da cotação (QR-B)
  '/transferencia/', // troca de titularidade — proprietário antigo autoriza/contesta
] as const;

// Prefixos "nus": `/quote` casa `/quote` e `/quote/:slug`, mas NÃO `/quotes`
// (rota autenticada do consultor) — exige que o próximo caractere seja `/` ou fim.
const PUBLIC_NO_PWA_BARE_PREFIXES = [
  '/quote', // landing pública de cotação (/quote, /quote/:slug)
  '/cotacao', // redirect legado PT → /quote
] as const;

/**
 * True quando o pathname pertence a um fluxo público de uso único que deve rodar
 * SEM PWA (sem service worker, sem manifest). Espelha a lógica de matching de
 * `isForcedLightPath` (@alamo/types) mas com um conjunto de rotas MAIOR: além das
 * forced-light, inclui `/transferencia/` e `/cadastro/`.
 */
export function isPublicNoPwaRoute(pathname: string): boolean {
  const slashMatch = PUBLIC_NO_PWA_SLASH_PREFIXES.some(
    (prefix) => pathname === prefix.slice(0, -1) || pathname.startsWith(prefix),
  );
  if (slashMatch) return true;
  return PUBLIC_NO_PWA_BARE_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
}

/**
 * Desliga o PWA na aba atual quando o boot cai numa rota pública de uso único:
 * remove o manifest (não-instalável) e desregistra/limpa qualquer service worker
 * + precache de uma visita anterior (assets sempre frescos). Idempotente e seguro
 * fora do browser (guardas de `window`/`navigator`). Chamado uma vez no boot
 * (main.tsx). Não registra SW novo — quem faz isso é o PwaUpdatePrompt, que o App
 * só monta fora dessas rotas.
 */
export function disablePwaForPublicRoute(): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  document.querySelectorAll('link[rel="manifest"]').forEach((link) => link.remove());

  const nav = window.navigator;
  if (nav && 'serviceWorker' in nav) {
    nav.serviceWorker
      .getRegistrations()
      .then((registrations) => {
        registrations.forEach((registration) => void registration.unregister());
      })
      .catch(() => undefined);
  }

  if ('caches' in window) {
    window.caches
      .keys()
      .then((keys) => {
        keys
          .filter((key) => key.startsWith('workbox') || key.startsWith('alamo'))
          .forEach((key) => void window.caches.delete(key));
      })
      .catch(() => undefined);
  }
}
